import React, { useEffect, useState, useCallback } from 'react';
import { ClockCounterClockwise, ArrowsClockwise, FunnelSimple } from '@phosphor-icons/react';
import { auditAPI } from '../../utils/api';

/**
 * Producer-facing activity feed for a single show. Shows who did what
 * and when across the most recent producer-side actions (entries marked
 * paid, scores recorded, draws generated/reversed, results published,
 * show state changes, show edits).
 *
 * Designed to live inside ShowDetail's "Manage Show State" stack so the
 * producer can spot at a glance whether an editor mis-marked an entry
 * or pushed the wrong class live without hunting through Mongo.
 */

const ROLE_PILL = {
  MASTER: { label: 'Admin', color: 'bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30' },
  OWNER: { label: 'Owner', color: 'bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30' },
  EDITOR: { label: 'Editor', color: 'bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/30' },
  USER: { label: 'User', color: 'bg-[#5E5E5E]/15 text-[#9E9E9E] border-[#5E5E5E]/30' },
};

const ACTION_LABEL = {
  'show.updated': 'edited the show',
  'show.state.draft': 'moved show to DRAFT',
  'show.state.published': 'published the show',
  'show.state.live': 'set show LIVE',
  'show.state.completed': 'marked show COMPLETED',
  'entry.payment.paid': 'marked entry PAID',
  'entry.payment.unpaid': 'marked entry UNPAID',
  'entry.payment.comp': 'marked entry COMP',
  'entry.deleted': 'deleted an entry',
  'class.entries.cleared': 'cleared all entries',
  'draw.generated': 'generated a draw',
  'draw.reversed': 'reversed a draw',
  'score.recorded': 'recorded a time',
  'score.nt': 'recorded NT',
  'score.scr': 'recorded SCR',
  'results.published': 'published results',
};

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const t = Date.parse(iso);
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(t).toLocaleDateString();
  } catch {
    return '';
  }
}

const FILTERS = [
  { key: '', label: 'All activity' },
  { key: 'entry.payment', label: 'Payments' },
  { key: 'score', label: 'Times' },
  { key: 'draw', label: 'Draws' },
  { key: 'results', label: 'Results' },
  { key: 'show', label: 'Show edits' },
];

export const ActivityFeed = ({ showId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    if (!showId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await auditAPI.list(showId, { limit: 100, actionPrefix: filter });
      setLogs(data.logs || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [showId, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4 sm:p-6" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-white font-bold text-lg flex items-center gap-2">
          <ClockCounterClockwise size={20} weight="duotone" className="text-[#0096FF]" />
          Activity Log
        </h2>
        <button
          onClick={load}
          className="text-[#9E9E9E] hover:text-white p-1.5 rounded transition-colors"
          title="Refresh"
          data-testid="activity-refresh-btn"
        >
          <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        <FunnelSimple size={14} className="text-[#5E5E5E] mt-1.5 mr-1" />
        {FILTERS.map((f) => (
          <button
            key={f.key || 'all'}
            onClick={() => setFilter(f.key)}
            data-testid={`activity-filter-${f.key || 'all'}`}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              filter === f.key
                ? 'bg-[#0096FF] text-white'
                : 'bg-[#2A2A2A] text-[#9E9E9E] hover:bg-[#3A3A3A] hover:text-white'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-[#FF3B30] text-sm mb-3" data-testid="activity-error">{error}</div>
      )}

      {loading && logs.length === 0 ? (
        <div className="text-[#5E5E5E] text-sm py-6 text-center">Loading activity…</div>
      ) : logs.length === 0 ? (
        <div className="text-[#5E5E5E] text-sm py-6 text-center" data-testid="activity-empty">
          No activity yet — actions on this show will show up here.
        </div>
      ) : (
        <ul className="divide-y divide-[#2A2A2A]" data-testid="activity-list">
          {logs.map((log) => {
            const pill = ROLE_PILL[log.actor_role] || ROLE_PILL.USER;
            const verb = ACTION_LABEL[log.action] || log.action.replaceAll('.', ' ');
            return (
              <li key={log.id} className="py-3 flex items-start gap-3" data-testid={`activity-row-${log.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide border rounded ${pill.color}`}>
                      {pill.label}
                    </span>
                    <span className="text-white text-sm font-medium truncate">
                      {log.actor_name || log.actor_email || 'Someone'}
                    </span>
                    <span className="text-[#9E9E9E] text-sm">{verb}</span>
                  </div>
                  {log.target_summary && (
                    <div className="text-[#BFBFBF] text-xs mt-1 truncate">{log.target_summary}</div>
                  )}
                </div>
                <div className="text-[#5E5E5E] text-xs whitespace-nowrap mt-0.5" title={log.created_at}>
                  {timeAgo(log.created_at)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ActivityFeed;
